import React, { useState } from 'react';
import { resolveProvider } from '../onboarding/providerCatalog';

/**
 * One tool's icon, resolved the same way everywhere.
 *
 * There were two half-systems: a simpleicons slug table local to My Business
 * (real logos, monogram fallback tinted brand blue) and `providerCatalog`
 * (per-provider brand colour and initial, no logo). Neither alone is right —
 * a real logo when we have one, and a monogram in the tool's OWN colour when
 * we don't, so a wall of unknown tools doesn't read as a wall of identical
 * blue squares.
 */

/** Tool value → simpleicons slug. Keys are normalised (lowercase, alphanumeric
 *  only), so 'Google Drive', 'google_drive' and 'googledrive' all resolve. */
const APP_ICON_SLUG: Record<string, string> = {
  slack: 'slack', hubspot: 'hubspot', stripe: 'stripe', salesforce: 'salesforce',
  notion: 'notion', gmail: 'gmail', shopify: 'shopify', intercom: 'intercom',
  zendesk: 'zendesk', asana: 'asana', trello: 'trello',
  aws: 'amazonaws', amazonaws: 'amazonaws', gcp: 'googlecloud', googlecloud: 'googlecloud',
  azure: 'microsoftazure', github: 'github', gitlab: 'gitlab', figma: 'figma',
  linear: 'linear', jira: 'jira', zoom: 'zoom', teams: 'microsoftteams',
  outlook: 'microsoftoutlook', airtable: 'airtable', monday: 'mondaydotcom',
  quickbooks: 'quickbooks', mailchimp: 'mailchimp', zapier: 'zapier',
  clickup: 'clickup', dropbox: 'dropbox', calendly: 'calendly',
  // The rest of the connector catalogue, so a searchable connector always has
  // the same icon on the stack card as it does on the connectors screen.
  googlecalendar: 'googlecalendar', outlookcalendar: 'microsoftoutlook',
  googledrive: 'googledrive', onedrive: 'microsoftonedrive',
  bitbucket: 'bitbucket', discord: 'discord',
  googleanalytics: 'googleanalytics', mixpanel: 'mixpanel',
};

/* Brands Simple Icons no longer carries (Microsoft, Slack, Salesforce, AWS):
   their own site icon instead, so the row shows a logo rather than a letter. */
const APP_ICON_DOMAIN: Record<string, string> = {
  outlook: 'outlook.com', outlookcalendar: 'outlook.com', onedrive: 'onedrive.live.com',
  teams: 'teams.microsoft.com', azure: 'azure.microsoft.com',
  slack: 'slack.com', salesforce: 'salesforce.com',
  aws: 'aws.amazon.com', amazonaws: 'aws.amazon.com',
};

export function appIconUrl(value: string): string | null {
  const key = String(value || '').toLowerCase().replace(/[^a-z0-9]/g, '');
  const domain = APP_ICON_DOMAIN[key];
  if (domain) return `https://www.google.com/s2/favicons?domain=${domain}&sz=64`;
  const slug = APP_ICON_SLUG[key];
  return slug ? `https://cdn.simpleicons.org/${slug}` : null;
}

export interface AppIconProps {
  /** Slug or free-text tool name. */
  value: string;
  /** Display label, used for the monogram and alt text. */
  label?: string;
  size?: number;
}

export const AppIcon: React.FC<AppIconProps> = ({ value, label, size = 18 }) => {
  const [failed, setFailed] = useState(false);
  const url = appIconUrl(value);

  if (url && !failed) {
    return (
      <img
        src={url}
        alt=""
        width={size}
        height={size}
        className="rounded-sm"
        onError={() => setFailed(true)}
      />
    );
  }

  // No logo: a monogram in the tool's own brand colour. `resolveProvider`
  // synthesizes a stable colour and Title Case label for anything it has never
  // heard of, so a tool the user typed still looks like a tool.
  const { provider } = resolveProvider(value);
  const text = (label || provider.label || '?').charAt(0).toUpperCase();
  return (
    <span
      aria-hidden="true"
      className="grid shrink-0 place-items-center rounded-sm font-bold"
      style={{
        width: size,
        height: size,
        background: provider.color,
        color: provider.textColor || '#fff',
        fontSize: Math.max(9, Math.round(size * 0.55)),
      }}
    >
      {text}
    </span>
  );
};

export default AppIcon;
